import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { batchDelete, batchList } from "../../../api/Batch";
import { courseList } from "../../../api/Courses";
import { LoadingDiv, SweetAlertShow } from "../../master/Master";
import { formatToDate, formatToTime } from "../../master/FormatToDateTime";
import usePrivateLink from "../../config/usePrivateLink";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import DownloadExcel from "../../master/DownloadExcel";
import { PrintPage } from "../../master/Print";
import AcademicPortalLayout from "../AcademicPortalLayout";
import BatchStatusUpdate from "./BatchStatusUpdate";

const STATUS_TABS = [
  { value: "", label: "All Batches", icon: "ri-apps-2-line" },
  { value: "0", label: "New", icon: "ri-add-circle-line" },
  { value: "1", label: "Running", icon: "ri-play-circle-line" },
  { value: "2", label: "Finished", icon: "ri-checkbox-circle-line" },
];
const STATUS_META = {
  0: { label: "New", icon: "ri-time-line", tone: "new" },
  1: { label: "Running", icon: "ri-play-circle-fill", tone: "running" },
  2: { label: "Finished", icon: "ri-checkbox-circle-fill", tone: "finished" },
};
const getStatusMeta = (status) => STATUS_META[String(status)] || { label: "Unknown", icon: "ri-question-line", tone: "unknown" };
const n = (value) => Number(value) || 0;
const safeDate = (value) => value ? formatToDate(value) : "—";
const safeTime = (value) => value ? formatToTime(value) : "—";
const money = (value) => `₹${n(value).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const errorMessage = (error) => error?.response?.data?.message || error?.response?.message || error?.message || "Something went wrong";
const readSessions = () => {
  try {
    const result = JSON.parse(localStorage.getItem("sessions") || "[]");
    return Array.isArray(result) ? result : [];
  } catch { return []; }
};
const initialFilters = () => ({
  status: "",
  session_id: String(readSessions().find((s) => Number(s.status) === 1)?.id ?? ""),
  course_id: "",
});
const studentsTotal = (batch) => {
  const explicit = batch?.batch_students_count;
  if (explicit !== null && explicit !== undefined) return n(explicit);
  return n(batch?.active_batch_students_count) + n(batch?.inactive_batch_students_count) +
    n(batch?.transfer_batch_students_count) + n(batch?.complete_batch_students_count) +
    n(batch?.dropout_batch_students_count);
};

function Capacity({ count, limit }) {
  if (limit <= 0) return <span className="sb-muted">No seat limit</span>;
  const percent = Math.min(100, Math.round((count / limit) * 100));
  return (
    <div className="sb-capacity">
      <div className="sb-progress"><span style={{ width: `${percent}%`, background: percent >= 100 ? "#e16b70" : percent >= 80 ? "#e7a44b" : "#6074df" }} /></div>
      <span className="sb-muted">{count} / {limit} seats · {percent}%</span>
    </div>
  );
}

function SummaryCard({ icon, label, value, variant, detail }) {
  return (
    <div className={`sb-summary sb-summary-${variant}`}>
      <div className="sb-summary-symbol"><i className={icon} /></div>
      <div className="sb-summary-copy"><span>{label}</span><strong>{value.toLocaleString("en-IN")}</strong><small>{detail}</small></div>
    </div>
  );
}

export default function Batches() {
  const hasAccess = usePrivateLink();
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [sessions] = useState(readSessions);
  const [courses, setCourses] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [search, setSearch] = useState("");
  const [modalData, setModalData] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((key) => key + 1), []);

  useEffect(() => {
    let cancelled = false;
    const fetchList = async () => {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) params.set(key, String(value));
      });
      try {
        const response = await batchList(params.toString() ? `&${params.toString()}` : "");
        if (cancelled) return;
        if (response?.data?.status) {
          setBatches(Array.isArray(response.data.data) ? response.data.data : []);
        } else {
          setBatches([]);
          SweetAlertShow("info", "Warning", response?.data?.message || "Unable to load batches");
        }
      } catch (error) {
        if (!cancelled) {
          setBatches([]);
          SweetAlertShow("error", "Error", errorMessage(error));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchList();
    return () => { cancelled = true; };
  }, [filters, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await courseList();
        if (!cancelled && response?.data?.status) setCourses(Array.isArray(response?.data?.data) ? response.data.data : []);
      } catch (error) {
        if (!cancelled) SweetAlertShow("error", "Error", errorMessage(error));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visibleBatches = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return batches;
    return batches.filter((b) => [b?.title, b?.course?.title, b?.sessions?.session, b?.class_teacher_info?.name, b?.id]
      .some((x) => String(x ?? "").toLowerCase().includes(term)));
  }, [batches, search]);

  const summary = useMemo(() => visibleBatches.reduce((sum, b) => {
    sum.running += Number(b?.status) === 1 ? 1 : 0;
    sum.active += n(b?.active_batch_students_count);
    sum.completed += n(b?.complete_batch_students_count);
    return sum;
  }, { running: 0, active: 0, completed: 0 }), [visibleBatches]);

  const pageCount = Math.max(1, Math.ceil(visibleBatches.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const pagedBatches = useMemo(() => visibleBatches.slice((safePage - 1) * pageSize, safePage * pageSize), [visibleBatches, safePage, pageSize]);
  useEffect(() => setPage(1), [filters, search, pageSize]);
  const filtersActive = filters.status !== "" || filters.course_id !== "" || search.trim() !== "" || filters.session_id !== initialFilters().session_id;
  const setFilter = (name, value) => setFilters((previous) => ({ ...previous, [name]: value }));
  const resetFilters = () => { setFilters(initialFilters()); setSearch(""); setPage(1); };

  const handleDelete = async (batch) => {
    if (studentsTotal(batch) > 0) {
      Swal.fire("Cannot delete", "This batch has enrolled students. Remove or transfer the student records first.", "info");
      return;
    }
    const confirm = await Swal.fire({
      title: "Delete this batch?", text: `“${batch?.title || "Batch"}” will be permanently deleted.`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Delete batch", cancelButtonText: "Cancel",
      confirmButtonColor: "#d64d62",
    });
    if (!confirm.isConfirmed) return;
    setLoading(true);
    try {
      const response = await batchDelete(batch.id);
      if (response?.data?.status) {
        await Swal.fire("Deleted", response?.data?.message || "Batch deleted successfully", "success");
        refresh();
      } else {
        Swal.fire("Warning", response?.data?.message || "Unable to delete batch", "info");
      }
    } catch (error) { Swal.fire("Error", errorMessage(error), "error"); }
    finally { setLoading(false); }
  };

  const handleExport = (type) => {
    if (!visibleBatches.length) {
      SweetAlertShow("info", "No records", "No batches found to export.");
      return;
    }
    const rows = visibleBatches.map((b, index) => ({
      "#": index + 1,
      "Batch Name": b?.title || "",
      "Course": b?.course?.title || "",
      "Session": b?.sessions?.session || "",
      "Batch Fee": b?.batch_fees ?? "",
      "Registration Fee": b?.batch_reg_fees ?? "",
      "Duration": b?.duration ?? "",
      "Start Date": safeDate(b?.starting_date),
      "Timing": `${safeTime(b?.start_time)} - ${safeTime(b?.end_time)}`,
      "Active Students": n(b?.active_batch_students_count),
      "Completed Students": n(b?.complete_batch_students_count),
      "Total Students": studentsTotal(b),
      "Batch Status": getStatusMeta(b?.status).label,
    }));
    try {
      if (type === "excel") DownloadExcel(rows, [], "Batch_List", []);
      else PrintPage(rows);
    } catch (error) { SweetAlertShow("error", "Export error", errorMessage(error)); }
  };

  return (
    <>
      <style>{`
        .sb-page { --sb-primary:#596cda; --sb-border:#e7eaf2; --sb-muted:#808a9e; color:#263249; }
        .sb-page .sb-card { background:#fff;border:1px solid var(--sb-border);border-radius:15px;box-shadow:0 3px 16px rgba(22,34,70,.035); }
        .sb-page .sb-top {padding:20px 22px 16px;display:flex;gap:16px;justify-content:space-between;align-items:center;flex-wrap:wrap;}
        .sb-page .sb-heading {display:flex;align-items:center;gap:13px;min-width:0;}
        .sb-page .sb-heading-icon {width:44px;height:44px;border-radius:12px;background:#edf0ff;color:#5369d6;display:grid;place-items:center;font-size:23px;flex-shrink:0;}
        .sb-page .sb-heading h4 {font-size:19px;font-weight:750;letter-spacing:-.45px;line-height:1.3;margin:0 0 2px;}
        .sb-page .sb-heading p {margin:0;color:var(--sb-muted);font-size:12px;}
        .sb-page .sb-actions {display:flex;align-items:center;flex-wrap:wrap;gap:8px;}
        .sb-page .sb-btn {display:inline-flex;align-items:center;justify-content:center;gap:7px;padding:9px 12px;border-radius:9px;border:1px solid #dfe4f0;background:#fff;color:#506078;font-size:12px;font-weight:650;text-decoration:none;cursor:pointer;white-space:nowrap;transition:.15s ease;}
        .sb-page .sb-btn:hover {background:#f5f7ff;border-color:#c9d3ff;color:#4d61c9;}
        .sb-page .sb-btn i {font-size:16px;line-height:1;}
        .sb-page .sb-btn-primary {background:#596cda;border-color:#596cda;color:#fff;box-shadow:0 3px 8px #596cda25;}
        .sb-page .sb-btn-primary:hover {background:#4c5fc9;border-color:#4c5fc9;color:#fff;}
        .sb-page .sb-btn:disabled {opacity:.45;cursor:not-allowed;}
        .sb-page .sb-divider {height:1px;background:#edf0f5;}
        .sb-page .sb-summary-grid {padding:15px 20px 19px;display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;}
        .sb-page .sb-summary {border:1px solid #edf0f5;background:#fbfcff;border-radius:12px;padding:13px 14px;display:flex;gap:12px;align-items:center;min-width:0;}
        .sb-page .sb-summary-symbol {width:43px;height:43px;display:grid;place-items:center;border-radius:11px;font-size:21px;flex-shrink:0;background:#eaf0ff;color:#5573df;}
        .sb-page .sb-summary-running .sb-summary-symbol {background:#e5f8ef;color:#219866;}
        .sb-page .sb-summary-active .sb-summary-symbol {background:#eaf6ff;color:#2788c4;}
        .sb-page .sb-summary-completed .sb-summary-symbol {background:#f0eefb;color:#8066c9;}
        .sb-page .sb-summary-copy {display:flex;flex-direction:column;min-width:0;}
        .sb-page .sb-summary-copy span {font-size:11px;color:#778299;}
        .sb-page .sb-summary-copy strong {font-size:22px;font-weight:750;line-height:1.3;color:#27334c;}
        .sb-page .sb-summary-copy small {font-size:10px;color:#98a0b0;}
        .sb-page .sb-main {margin-top:15px;overflow:hidden;}
        .sb-page .sb-filters {padding:16px 20px 17px;}
        .sb-page .sb-section-head {display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-bottom:13px;}
        .sb-page .sb-section-title {font-size:14px;margin:0;font-weight:750;letter-spacing:-.1px;color:#29364e;}
        .sb-page .sb-section-note {font-size:11px;color:var(--sb-muted);}
        .sb-page .sb-status-tabs {display:flex;gap:6px;overflow-x:auto;scrollbar-width:none;padding-bottom:2px;margin-bottom:13px;}
        .sb-page .sb-status-tabs::-webkit-scrollbar {display:none;}
        .sb-page .sb-status-tab {display:inline-flex;align-items:center;gap:7px;padding:8px 13px;white-space:nowrap;border:1px solid #e8ebf3;background:#f8f9fc;color:#748096;border-radius:9px;font-size:12px;font-weight:650;cursor:pointer;transition:.15s;}
        .sb-page .sb-status-tab i {font-size:16px;}
        .sb-page .sb-status-tab:hover {background:#eff2fd;color:#5367cd;}
        .sb-page .sb-status-tab.selected {background:#eff2ff;border-color:#d0d9ff;color:#4d62ca;box-shadow:inset 0 0 0 1px #dbe2ff;}
        .sb-page .sb-filter-grid {display:grid;grid-template-columns:1fr 1fr minmax(200px,1.35fr) auto;gap:10px;align-items:end;}
        .sb-page .sb-field label {font-size:11px;font-weight:650;color:#657187;margin-bottom:5px;display:block;}
        .sb-page .sb-field select,.sb-page .sb-field input {height:40px;width:100%;border:1px solid #e0e5ee;border-radius:9px;background:#fff;color:#3b4960;padding:0 11px;font-size:12px;outline:none;min-width:0;}
        .sb-page .sb-field select:focus,.sb-page .sb-field input:focus {border-color:#9aaafa;box-shadow:0 0 0 3px #6175ec17;}
        .sb-page .sb-search-input {position:relative;}.sb-page .sb-search-input input {padding-left:34px;padding-right:32px;}
        .sb-page .sb-search-input > i {position:absolute;left:11px;top:11px;font-size:17px;color:#9aa4b5;}
        .sb-page .sb-search-clear {position:absolute;right:7px;top:7px;width:26px;height:26px;border:0;background:transparent;border-radius:6px;color:#909aad;}
        .sb-page .sb-reset {height:40px;}
        .sb-page .sb-table-toolbar {padding:14px 20px;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;}
        .sb-page .sb-table-toolbar p {font-size:11px;color:var(--sb-muted);margin:2px 0 0;}
        .sb-page .sb-count {display:inline-flex;align-items:center;justify-content:center;margin-left:7px;font-size:11px;background:#eef1ff;color:#5968c8;padding:3px 8px;border-radius:7px;}
        .sb-page .sb-table-scroll {width:100%;overflow-x:auto;}
        .sb-page .sb-table {width:100%;border-collapse:separate;border-spacing:0;min-width:910px;}
        .sb-page .sb-table thead th {background:#f8f9fc;padding:12px 15px;text-transform:uppercase;letter-spacing:.45px;font-size:10px;color:#8190a5;font-weight:750;white-space:nowrap;border-top:1px solid #edf0f5;border-bottom:1px solid #edf0f5;}
        .sb-page .sb-table tbody td {padding:14px 15px;border-bottom:1px solid #f0f2f6;vertical-align:middle;font-size:12px;color:#526078;}
        .sb-page .sb-table tbody tr:hover {background:#fafbff;}
        .sb-page .sb-table tbody tr:last-child td {border-bottom:0;}
        .sb-page .sb-row-index {color:#a0a9ba;font-weight:650;}
        .sb-page .sb-batch-name {display:block;color:#3b55b3;text-decoration:none;font-size:13px;font-weight:750;line-height:1.35;margin-bottom:4px;}
        .sb-page .sb-batch-name:hover {color:#263b9c;text-decoration:underline;}
        .sb-page .sb-batch-name-static {color:#2f3b55;}
        .sb-page .sb-course {display:block;color:#8793a7;font-size:11px;}
        .sb-page .sb-muted {font-size:10.5px;color:#9ca5b4;}
        .sb-page .sb-numbers {display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
        .sb-page .sb-number {display:inline-flex;align-items:center;gap:5px;border-radius:6px;padding:4px 7px;font-size:11px;font-weight:700;background:#ebf9f1;color:#298760;}
        .sb-page .sb-number-complete {background:#eef1fb;color:#6a75ac;}
        .sb-page .sb-capacity {margin-top:8px;max-width:190px;}
        .sb-page .sb-progress {height:5px;border-radius:8px;background:#ecedf3;overflow:hidden;margin-bottom:4px;}
        .sb-page .sb-progress span {height:100%;border-radius:8px;display:block;}
        .sb-page .sb-date {display:flex;align-items:center;gap:6px;white-space:nowrap;color:#626e82;}
        .sb-page .sb-date i {color:#9ba8ba;font-size:15px;}
        .sb-page .sb-status {display:inline-flex;align-items:center;gap:6px;border-radius:7px;padding:6px 9px;font-size:11px;font-weight:750;border:1px solid transparent;white-space:nowrap;}
        .sb-page .sb-status-running {background:#e9f8ef;color:#268657;border-color:#daf1e3;}
        .sb-page .sb-status-new {background:#edf3ff;color:#557ac0;border-color:#e2ecff;}
        .sb-page .sb-status-finished {background:#f0f1f5;color:#68748a;border-color:#e7e9ee;}
        .sb-page .sb-status-unknown {background:#f5f5f5;color:#777;}
        .sb-page button.sb-status {cursor:pointer;}.sb-page button.sb-status:hover {filter:brightness(.97);}
        .sb-page .sb-row-actions {display:flex;gap:5px;align-items:center;}
        .sb-page .sb-icon-btn {display:inline-grid;place-items:center;width:31px;height:31px;border:1px solid #e5e9f1;border-radius:8px;background:#fff;color:#66778f;text-decoration:none;font-size:16px;cursor:pointer;}
        .sb-page .sb-icon-btn:hover {background:#eff3ff;color:#5368d0;border-color:#dce4ff;}
        .sb-page .sb-icon-btn-danger:hover {background:#fff0f2;color:#cd4c61;border-color:#ffdce2;}
        .sb-page .sb-empty {padding:56px 15px;text-align:center;}.sb-page .sb-empty > i {font-size:38px;color:#adb8ca;}
        .sb-page .sb-empty h5 {font-size:15px;margin:12px 0 4px;}.sb-page .sb-empty p {font-size:12px;color:#8f9bad;margin-bottom:15px;}
        .sb-page .sb-footer {padding:13px 20px;border-top:1px solid #edf0f5;display:flex;gap:12px;justify-content:space-between;align-items:center;flex-wrap:wrap;}
        .sb-page .sb-footer-text {font-size:11px;color:#8b96a8;}
        .sb-page .sb-page-controls {display:flex;gap:7px;align-items:center;}.sb-page .sb-page-number {font-size:11px;color:#63728c;font-weight:650;min-width:75px;text-align:center;}
        .sb-page .sb-page-size {border:1px solid #e1e6f0;border-radius:8px;background:white;height:32px;color:#66758c;font-size:11px;padding:0 8px;}
        @media(max-width:1199px){.sb-page .sb-summary-grid {grid-template-columns:repeat(2,minmax(0,1fr));}.sb-page .sb-filter-grid {grid-template-columns:repeat(2,minmax(0,1fr));}.sb-page .sb-reset {justify-self:start;}}
        @media(max-width:575px){.sb-page .sb-top {padding:15px;}.sb-page .sb-heading h4 {font-size:17px;}.sb-page .sb-heading-icon {height:39px;width:39px;font-size:20px;}.sb-page .sb-actions {width:100%;}.sb-page .sb-actions .sb-btn {flex:1;padding:9px 7px;font-size:11px;}.sb-page .sb-summary-grid {padding:12px;gap:8px;}.sb-page .sb-summary {padding:11px 9px;gap:7px;}.sb-page .sb-summary-symbol {width:34px;height:34px;font-size:18px;}.sb-page .sb-summary-copy strong {font-size:18px;}.sb-page .sb-summary-copy span {font-size:10px;}.sb-page .sb-summary-copy small {font-size:9px;}.sb-page .sb-filters,.sb-page .sb-table-toolbar {padding:13px;}.sb-page .sb-filter-grid {grid-template-columns:1fr;}.sb-page .sb-reset {width:100%;}.sb-page .sb-footer {padding:12px;}}
      `}</style>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <AcademicPortalLayout />
                  <div className="col-12 sb-page">
                    <section className="sb-card">
                      <div className="sb-top">
                        <div className="sb-heading">
                          <div className="sb-heading-icon"><i className="ri-group-2-line" /></div>
                          <div><h4>Batch Management</h4><p>Manage classes, schedules, enrollments and batch status</p></div>
                        </div>
                        <div className="sb-actions">
                          <button type="button" className="sb-btn" onClick={refresh} title="Refresh batches"><i className="ri-refresh-line" /> Refresh</button>
                          {hasAccess("/batches/export") && <button type="button" className="sb-btn" onClick={() => handleExport("excel")}><i className="ri-file-excel-2-line" /> Export</button>}
                          {hasAccess("/batches/print") && <button type="button" className="sb-btn" onClick={() => handleExport("print")}><i className="ri-printer-line" /> Print</button>}
                          {hasAccess("/batch/add") && <Link to="/batch/add" className="sb-btn sb-btn-primary"><i className="ri-add-line" /> Add Batch</Link>}
                        </div>
                      </div>
                      <div className="sb-divider" />
                      <div className="sb-summary-grid">
                        <SummaryCard icon="ri-stack-line" variant="batches" label="Total Batches" value={visibleBatches.length} detail="In current results" />
                        <SummaryCard icon="ri-play-circle-line" variant="running" label="Running Batches" value={summary.running} detail="Currently active" />
                        <SummaryCard icon="ri-group-line" variant="active" label="Active Students" value={summary.active} detail="Across these batches" />
                        <SummaryCard icon="ri-graduation-cap-line" variant="completed" label="Completed Students" value={summary.completed} detail="Across these batches" />
                      </div>
                    </section>

                    <section className="sb-card sb-main">
                      <div className="sb-filters">
                        <div className="sb-section-head"><h5 className="sb-section-title">Find & filter batches</h5><span className="sb-section-note">Choose status, session or class</span></div>
                        <div className="sb-status-tabs" role="group" aria-label="Filter by batch status">
                          {STATUS_TABS.map((tab) => (
                            <button type="button" key={tab.value} className={`sb-status-tab ${filters.status === tab.value ? "selected" : ""}`} onClick={() => setFilter("status", tab.value)} aria-pressed={filters.status === tab.value}>
                              <i className={tab.icon} /> {tab.label}
                            </button>
                          ))}
                        </div>
                        <div className="sb-filter-grid">
                          <div className="sb-field"><label htmlFor="sb-session">Academic Session</label><select id="sb-session" value={filters.session_id} onChange={(e) => setFilter("session_id", e.target.value)}><option value="">All Sessions</option>{sessions.map((session) => <option key={session.id} value={session.id}>{session.session}{Number(session.status) === 1 ? " (Active)" : ""}</option>)}</select></div>
                          <div className="sb-field"><label htmlFor="sb-course">Course / Class</label><select id="sb-course" value={filters.course_id} onChange={(e) => setFilter("course_id", e.target.value)}><option value="">All Courses</option>{courses.map((course) => <option key={course.id} value={course.id}>{course.title}</option>)}</select></div>
                          <div className="sb-field"><label htmlFor="sb-search">Search Batches</label><div className="sb-search-input"><i className="ri-search-line" /><input id="sb-search" type="search" placeholder="Batch name, class, teacher..." value={search} onChange={(e) => setSearch(e.target.value)} />{search && <button type="button" aria-label="Clear search" className="sb-search-clear" onClick={() => setSearch("")}><i className="ri-close-line" /></button>}</div></div>
                          {filtersActive && <button type="button" className="sb-btn sb-reset" onClick={resetFilters}><i className="ri-filter-off-line" /> Clear</button>}
                        </div>
                      </div>
                      <div className="sb-divider" />
                      <div className="sb-table-toolbar">
                        <div><h5 className="sb-section-title">Batch List <span className="sb-count">{visibleBatches.length}</span></h5><p>View batch details or manage status and settings</p></div>
                        <span className="sb-section-note">{visibleBatches.length ? `Showing ${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, visibleBatches.length)} of ${visibleBatches.length}` : "No records"}</span>
                      </div>
                      <div className="sb-table-scroll">
                        <table className="sb-table">
                          <thead><tr><th>#</th><th>Batch / Course</th><th>Students / Capacity</th><th>Timing</th><th>Start Date</th><th>Status</th><th>Actions</th></tr></thead>
                          <tbody>
                            {pagedBatches.length ? pagedBatches.map((batch, index) => {
                              const status = getStatusMeta(batch.status);
                              const active = n(batch.active_batch_students_count);
                              const complete = n(batch.complete_batch_students_count);
                              const limit = n(batch.student_limit);
                              const canDelete = studentsTotal(batch) === 0;
                              return <tr key={batch.id}>
                                <td className="sb-row-index">{(safePage - 1) * pageSize + index + 1}</td>
                                <td>
                                  {hasAccess("/batch/detail") ? <Link to={`/batch/detail?id=${encodeURIComponent(batch.id)}`} className="sb-batch-name">{batch.title || "Untitled Batch"}</Link> : <span className="sb-batch-name sb-batch-name-static">{batch.title || "Untitled Batch"}</span>}
                                  <span className="sb-course">{batch?.course?.title || "Course not assigned"}</span>
                                  {batch?.duration && <span className="sb-muted">Duration: {batch.duration} months</span>}
                                </td>
                                <td><div className="sb-numbers"><span className="sb-number"><i className="ri-user-follow-line" /> {active} Active</span><span className="sb-number sb-number-complete"><i className="ri-checkbox-circle-line" /> {complete} Completed</span></div><Capacity count={active} limit={limit} /></td>
                                <td><div className="sb-date"><i className="ri-time-line" /> {safeTime(batch.start_time)}</div><span className="sb-muted">to {safeTime(batch.end_time)}</span></td>
                                <td><div className="sb-date"><i className="ri-calendar-line" /> {safeDate(batch.starting_date)}</div></td>
                                <td>{hasAccess("/batches/status") ? <button type="button" className={`sb-status sb-status-${status.tone}`} title="Update batch status" data-bs-toggle="modal" data-bs-target="#editStatusModal" onClick={() => setModalData(batch)}><i className={status.icon} />{status.label}<i className="ri-arrow-down-s-line" /></button> : <span className={`sb-status sb-status-${status.tone}`}><i className={status.icon} />{status.label}</span>}</td>
                                <td><div className="sb-row-actions">
                                  {hasAccess("/batch/detail") && <Link className="sb-icon-btn" to={`/batch/detail?id=${encodeURIComponent(batch.id)}`} title="View students and details" aria-label={`View ${batch.title}`}><i className="ri-eye-line" /></Link>}
                                  {hasAccess("/batch/update") && <Link className="sb-icon-btn" to={`/batch/update?id=${encodeURIComponent(batch.id)}`} title="Edit batch" aria-label={`Edit ${batch.title}`}><i className="ri-edit-2-line" /></Link>}
                                  {hasAccess("/batch/delete") && canDelete && <button type="button" className="sb-icon-btn sb-icon-btn-danger" title="Delete batch" aria-label={`Delete ${batch.title}`} onClick={() => handleDelete(batch)}><i className="ri-delete-bin-6-line" /></button>}
                                </div></td>
                              </tr>;
                            }) : <tr><td colSpan={7}><div className="sb-empty"><i className="ri-folder-open-line" /><h5>No batches found</h5><p>{filtersActive ? "Try changing your search or clearing the filters." : "Create a new batch to get started."}</p>{filtersActive ? <button type="button" className="sb-btn" onClick={resetFilters}><i className="ri-refresh-line" /> Reset Filters</button> : hasAccess("/batch/add") ? <Link to="/batch/add" className="sb-btn sb-btn-primary"><i className="ri-add-line" /> Add Batch</Link> : null}</div></td></tr>}
                          </tbody>
                        </table>
                      </div>
                      <div className="sb-footer">
                        <div className="sb-footer-text">Showing {pagedBatches.length} of {visibleBatches.length} batches</div>
                        <div className="sb-page-controls">
                          <select aria-label="Rows per page" className="sb-page-size" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}><option value={10}>10 rows</option><option value={20}>20 rows</option><option value={50}>50 rows</option><option value={100}>100 rows</option></select>
                          <button type="button" className="sb-icon-btn" disabled={safePage <= 1} onClick={() => setPage(1)} aria-label="First page"><i className="ri-skip-back-line" /></button>
                          <button type="button" className="sb-icon-btn" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page"><i className="ri-arrow-left-s-line" /></button>
                          <span className="sb-page-number">{safePage} / {pageCount}</span>
                          <button type="button" className="sb-icon-btn" disabled={safePage >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))} aria-label="Next page"><i className="ri-arrow-right-s-line" /></button>
                          <button type="button" className="sb-icon-btn" disabled={safePage >= pageCount} onClick={() => setPage(pageCount)} aria-label="Last page"><i className="ri-skip-forward-line" /></button>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <BatchStatusUpdate modalData={modalData} handleFunction={refresh} />
    </>
  );
}
